import React from 'react'
import ValidatedDynamicFormField from '../ValidatedDynamicFormField'
import { FormFieldMapping } from '../../services/fieldMappingService'

interface OwnerFormSectionProps {
  title: string
  fields: FormFieldMapping[]
  formData: { [key: string]: any }
  fieldPrefix: string // "", "owner2_", "owner3_"
  errors: { [key: string]: string }
  onChange: (fieldName: string, value: any) => void
  canRemove: boolean
  onRemove?: () => void
}

const OwnerFormSection: React.FC<OwnerFormSectionProps> = ({
  title,
  fields,
  formData,
  fieldPrefix,
  errors,
  onChange,
  canRemove,
  onRemove
}) => {
  // Map Salesforce field types to form input types (same as DynamicFormSection)
  const mapSalesforceTypeToInputType = (salesforceType: string): string => {
    switch (salesforceType) {
      case 'string':
      case 'text': 
        return 'text'
      case 'textarea':
        return 'textarea'
      case 'email':
        return 'email'
      case 'phone':
        return 'phone'
      case 'url':
        return 'url'
      case 'select':
      case 'picklist':
        return 'select'
      case 'boolean':
        return 'boolean'
      case 'date':
      case 'datetime':
        return 'date'
      case 'number':
      case 'double':
      case 'int':
        return 'number'
      case 'currency':
        return 'currency'
      case 'percent':
        return 'percentage'
      default:
        return 'text'
    }
  }

  // Get effective field type for a field
  const getEffectiveFieldType = (field: FormFieldMapping): string => {
    return field.salesforceFieldType 
      ? mapSalesforceTypeToInputType(field.salesforceFieldType)
      : field.formFieldType
  }

  // Sort fields by display order
  const sortedFields = fields.sort((a, b) => {
    if (a.displayOrder !== b.displayOrder) {
      return a.displayOrder - b.displayOrder
    }
    return a.formFieldLabel.localeCompare(b.formFieldLabel)
  })

  return (
    <div className="owner-form-section">
      <div className="clean-form-layout">
        <div className="clean-form-sidebar">
          <h3 className="clean-form-sidebar-title">{title}</h3>
          <p className="clean-form-sidebar-description">
            {title === 'Primary Owner' 
              ? 'Primary contact information for this application.'
              : 'Additional owner information for this business.'
            }
          </p>
          {canRemove && onRemove && (
            <button 
              type="button" 
              className="btn btn-sm btn-outline-danger owner-remove-btn-sidebar" 
              onClick={onRemove}
              title={`Remove ${title}`}
            >
              <i className="bi bi-x-lg me-1"></i> Remove Owner
            </button>
          )}
        </div>
        
        <div className="clean-form-container">
          <div className="clean-form-grid">
            {sortedFields.map((field) => {
              const fieldName = `${fieldPrefix}${field.formFieldName}`
              
              return (
                <div 
                  key={fieldName}
                  className={getEffectiveFieldType(field) === 'textarea' ? 'clean-form-field-full' : 'clean-form-field-half'}
                >
                  <ValidatedDynamicFormField
                    field={{
                      ...field,
                      formFieldName: fieldName // Use prefixed field name for validation
                    }}
                    value={formData[fieldName] || ''}
                    onChange={onChange}
                    error={errors[fieldName]}
                  />
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default OwnerFormSection
